import type { UseQueryOptions } from '@tanstack/react-query';

import type { CplatApiResponse, InquiryItem, ReviewItem } from '@/types/api';
import { MARKET_FUNCTION_SUPPORT } from '@/pages/Market/_constants/const';
import { type MarketStoreListItem, SELECT_ALL_MARKET } from '../_consts/constants';
import type { TabKey as InquiryTabKey } from '../_consts/inquiryConstants';
import type { TabKey as ReviewTabKey } from '../_consts/reviewsConstants';
import { getMarketKey } from '../_utils/getMarketKey';

const MARKET_ICON_URLS = {
  smartstore:
    'https://cplat-images.s3.ap-northeast-2.amazonaws.com/sellerbox/market-icon/IconMarketSmartstore.png',
  sellerbox:
    'https://cplat-images.s3.ap-northeast-2.amazonaws.com/sellerbox/market-icon/IconMarketCoupang.png',
  leader:
    'https://cplat-images.s3.ap-northeast-2.amazonaws.com/sellerbox/market-icon/IconMarket11st.png',
  gmarket:
    'https://cplat-images.s3.ap-northeast-2.amazonaws.com/sellerbox/market-icon/IconMarketGmarket.png',
} as const;

const PREVIEW_SOURCE_START_DATE_KEY = '2026-04-28';
const DAY_IN_MS = 24 * 60 * 60 * 1000;

const formatDateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseDateKey = (dateKey: string) => new Date(`${dateKey}T00:00:00+09:00`);

const getDateDistanceDays = (startDateKey: string, endDateKey: string) => {
  return Math.round(
    (parseDateKey(endDateKey).getTime() - parseDateKey(startDateKey).getTime()) / DAY_IN_MS,
  );
};

const getPositiveModulo = (value: number, divisor: number) =>
  ((value % divisor) + divisor) % divisor;

const addDays = (date: Date, days: number) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
};

type MembershipReviewPreviewDetailData = {
  data: Array<{
    ReportDetailList: ReviewItem[];
  }>;
};

const createReviewPreviewEndDate = () => {
  const endDate = new Date();
  endDate.setHours(0, 0, 0, 0);
  return endDate;
};

const createReviewPreviewStartDate = (endDate = createReviewPreviewEndDate()) =>
  addDays(endDate, -6);

const previewEndDate = createReviewPreviewEndDate();
const previewStartDate = createReviewPreviewStartDate(previewEndDate);

export const membershipReviewInquiryPreviewDates = {
  startDate: previewStartDate,
  endDate: previewEndDate,
};

export const membershipReviewInquiryPreviewMarkets: MarketStoreListItem[] = [
  SELECT_ALL_MARKET,
  {
    MarketId: 1,
    MarketName: '스마트스토어',
    MarketNameEnglish: 'smartstore',
    MarketIconUrl: MARKET_ICON_URLS.smartstore,
    StoreId: 'sellerbox-store',
    StoreName: '셀러박스',
    MarketAlias: '셀러박스',
    MarketSeqNo: 1,
  },
  {
    MarketId: 2,
    MarketName: '셀러박스',
    MarketNameEnglish: 'sellerbox',
    MarketIconUrl: MARKET_ICON_URLS.sellerbox,
    StoreId: 'sellerbox-direct',
    StoreName: 'sellerbox',
    MarketAlias: 'sellerbox',
    MarketSeqNo: 2,
  },
  {
    MarketId: 3,
    MarketName: '11번가',
    MarketNameEnglish: '11st',
    MarketIconUrl: MARKET_ICON_URLS.leader,
    StoreId: 'leader-store',
    StoreName: 'Leader',
    MarketAlias: 'Leader',
    MarketSeqNo: 3,
  },
  {
    MarketId: 4,
    MarketName: 'G마켓',
    MarketNameEnglish: 'gmarket',
    MarketIconUrl: MARKET_ICON_URLS.gmarket,
    StoreId: 'active-market',
    StoreName: '활기찬마켓',
    MarketAlias: '활기찬마켓',
    MarketSeqNo: 4,
  },
];

const primaryMarket = membershipReviewInquiryPreviewMarkets[1];

const createReview = ({
  reviewNo,
  reviewDate,
  productName,
  productOption,
  content,
  comment,
  commentDate,
  isMarketCommentEnable = true,
}: {
  reviewNo: string;
  reviewDate: string;
  productName: string;
  productOption?: string;
  content: string;
  comment?: string;
  commentDate?: string;
  isMarketCommentEnable?: boolean;
}): ReviewItem => ({
  ReviewNo: reviewNo,
  MarketId: primaryMarket.MarketId,
  StoreId: primaryMarket.StoreId,
  StoreName: primaryMarket.StoreName,
  MarketNameEnglish: primaryMarket.MarketNameEnglish,
  MarketIconUrl: primaryMarket.MarketIconUrl,
  ProductName: productName,
  ProductOption: productOption,
  Content: content,
  ReviewDate: reviewDate,
  ReviewScore: 5,
  ReviewCommentList:
    comment && commentDate
      ? [
          {
            comment_create_date: commentDate,
            review_comment: comment,
          },
        ]
      : [],
  AttachmentList: [],
  IsMarketCommentEnable: isMarketCommentEnable,
  EsmplusReviewType: '',
  EsmplusInternalId: '',
  EsmplusProductNo: '',
});

export const membershipReviewPreviewList: ReviewItem[] = [
  createReview({
    reviewNo: 'membership-review-001',
    reviewDate: '2026-04-28T17:50:00+09:00',
    productName: '프리미엄 무선 이어폰 X9 Pro',
    productOption: 'RED',
    content:
      '배터리도 오래가고 음질도 훌륭해요. 노이즈 캔슬링도 잘 작동합니다! 배터리도 오래가고 음질도 훌륭해요. 노이즈 캔슬링도 잘 작동합니다! 배터리도 오래가고 음질도 훌륭해요. 노이즈 캔슬링도 잘 작동합니다!',
  }),
  createReview({
    reviewNo: 'membership-review-002',
    reviewDate: '2026-04-29T09:30:00+09:00',
    productName: '스마트 LED 스탠드 조명 S3',
    content:
      '디자인이 너무 예뻐요! 밝기 조절이 섬세하고 눈이 전혀 피로하지 않아요. 따뜻한 조명 색상으로 밤새 작업해도 편안합니다. 강력 추천!',
  }),
  createReview({
    reviewNo: 'membership-review-003',
    reviewDate: '2026-04-30T14:22:00+09:00',
    productName: '접이식 알루미늄 노트북 거치대',
    content:
      '생각보다 훨씬 견고해요. 각도 조절도 부드럽고 설치도 10분 만에 뚝딱! 노트북 열도 확실히 줄어든 것 같아요. 재구매 의향 있습니다.',
    isMarketCommentEnable: false,
  }),
  createReview({
    reviewNo: 'membership-review-004',
    reviewDate: '2026-05-01T11:15:00+09:00',
    productName: '고속충전 USB-C 케이블 2m',
    content:
      '충전 속도 차이가 확실히 느껴져요. 기존 케이블보다 2배 정도 빠른 것 같고 케이블도 두껍고 튼튼해서 만족합니다.',
    comment: '구매해주셔서 감사합니다. 좋은 하루되세요.',
    commentDate: '2026-05-02T16:40:00+09:00',
  }),
];

const createReviewPreviewDateWindow = () => {
  const startDateKey = formatDateKey(previewStartDate);
  const endDateKey = formatDateKey(previewEndDate);

  return {
    startDateKey,
    endDateKey,
    rangeLengthDays: Math.max(1, getDateDistanceDays(startDateKey, endDateKey) + 1),
  };
};

const mapMembershipPreviewDateToWindow = (
  dateTime: string,
  dateWindow: ReturnType<typeof createReviewPreviewDateWindow>,
) => {
  const sourceDateKey = dateTime.slice(0, 10);
  const sourceOffset = getDateDistanceDays(PREVIEW_SOURCE_START_DATE_KEY, sourceDateKey);
  const windowOffset = getPositiveModulo(sourceOffset, dateWindow.rangeLengthDays);
  const mappedDate = addDays(parseDateKey(dateWindow.startDateKey), windowOffset);

  return `${formatDateKey(mappedDate)}T${dateTime.slice(11, 19)}+09:00`;
};

const remapMembershipReviewPreviewDates = (reviews: ReviewItem[]) => {
  const dateWindow = createReviewPreviewDateWindow();

  return reviews.map((review) => ({
    ...review,
    ReviewDate: mapMembershipPreviewDateToWindow(review.ReviewDate, dateWindow),
    ReviewCommentList: (review.ReviewCommentList ?? []).map((comment) => ({
      ...comment,
      comment_create_date: mapMembershipPreviewDateToWindow(comment.comment_create_date, dateWindow),
    })),
  }));
};

export const membershipReviewPreviewCounts = {
  total: membershipReviewPreviewList.length,
  notYet: membershipReviewPreviewList.filter(
    (review) => !review.ReviewCommentList || review.ReviewCommentList.length === 0,
  ).length,
  completed: membershipReviewPreviewList.filter(
    (review) => review.ReviewCommentList && review.ReviewCommentList.length > 0,
  ).length,
};

export const membershipReviewPreviewTabCounts: {
  key: ReviewTabKey;
  label: string;
  count: number;
}[] = [
  { key: 'whole', label: '전체 리뷰', count: membershipReviewPreviewCounts.total },
  { key: 'not-yet', label: '미답변', count: membershipReviewPreviewCounts.notYet },
  { key: 'completed', label: '답변완료', count: membershipReviewPreviewCounts.completed },
];

export const membershipReviewPreviewMarketInfoList = Object.entries(MARKET_FUNCTION_SUPPORT).map(
  ([marketNameEnglish, market]) => ({
    MarketNameEnglish: marketNameEnglish,
    MarketIconUrl: market.marketIconUrl,
  }),
);

const createInquiry = ({
  inquiryNo,
  inquiryDate,
  productName,
  title,
  content,
  checkedYn = 'N',
  qnaTypeCode = '상품문의',
  comment,
  commentDate,
}: {
  inquiryNo: string;
  inquiryDate: string;
  productName: string;
  title: string;
  content: string;
  checkedYn?: 'Y' | 'N';
  qnaTypeCode?: string;
  comment?: string;
  commentDate?: string;
}): InquiryItem => ({
  InquiryNo: inquiryNo,
  MarketId: primaryMarket.MarketId,
  StoreId: primaryMarket.StoreId,
  StoreName: primaryMarket.StoreName,
  MarketNameEnglish: primaryMarket.MarketNameEnglish,
  MarketIconUrl: primaryMarket.MarketIconUrl,
  ProductNo: `membership-product-${inquiryNo}`,
  ProductName: productName,
  ProductItemNo: '',
  Title: title,
  Content: content,
  InquiryDate: inquiryDate,
  CheckedYn: checkedYn,
  QnaTypeCode: qnaTypeCode,
  InquiryType: qnaTypeCode,
  InquiryCommentList:
    checkedYn === 'Y' && comment && commentDate
      ? [
          {
            InquiryCommentDate: commentDate,
            InquiryComment: comment,
          },
        ]
      : [],
  CoupangWingId: '',
  EsmToken: '',
  SellerAdminPageUrl: 'https://app.sellerbox.io',
});

const completedInquiryComments = [
  '배송 일정 확인 후 안내드렸습니다. 출고 준비가 완료되면 알림으로 다시 안내드릴게요.',
  '상품 구성품 기준으로 답변드렸습니다. 추가로 궁금한 점이 있으면 언제든 문의해주세요.',
  '교환 가능 여부를 확인해 안내드렸습니다. 접수 조건에 맞춰 처리 도와드릴게요.',
  '재고 상태 확인 후 구매 가능한 옵션을 안내드렸습니다.',
  '사이즈 선택 기준과 착용감을 함께 안내드렸습니다.',
  '설치 방법과 초기 설정 순서를 정리해 답변드렸습니다.',
  '배송지 변경 가능 시간을 확인해 안내드렸습니다.',
  '쿠폰 적용 조건과 결제 금액 기준을 확인해 답변드렸습니다.',
  '상품 사용 방법과 주의사항을 함께 안내드렸습니다.',
  'AS 접수 절차와 필요 정보를 정리해 답변드렸습니다.',
  '부분 취소 가능 여부를 확인해 안내드렸습니다.',
  '옵션별 색상 차이를 확인해 답변드렸습니다.',
  '예약 발송 일정과 도착 예상일을 안내드렸습니다.',
  '호환 가능한 모델 정보를 확인해 답변드렸습니다.',
  '포장 상태와 선물 포장 가능 여부를 안내드렸습니다.',
] as const;

const completedInquiries = Array.from({ length: 15 }, (_, index) =>
  createInquiry({
    inquiryNo: `membership-inquiry-completed-${index + 1}`,
    inquiryDate: `2026-05-${String((index % 4) + 1).padStart(2, '0')}T10:${String(index).padStart(
      2,
      '0',
    )}:00+09:00`,
    productName: '프리미엄 무선 이어폰 X9 Pro',
    title: '[상품문의] 배송 및 구성품 문의',
    content: '구성품과 배송 일정 문의드립니다.',
    checkedYn: 'Y',
    comment: completedInquiryComments[index],
    commentDate: `2026-05-${String((index % 4) + 1).padStart(2, '0')}T16:${String(index).padStart(
      2,
      '0',
    )}:00+09:00`,
  }),
);

export const membershipInquiryPreviewList: InquiryItem[] = [
  createInquiry({
    inquiryNo: 'membership-inquiry-001',
    inquiryDate: '2026-04-28T17:50:00+09:00',
    productName: '프리미엄 무선 이어폰 X9 Pro',
    title: '[상품문의] 이어폰 블루투스 연결이 자꾸 끊겨요',
    content:
      '어제 구매한 이어폰인데 핸드폰과 블루투스 연결이 자꾸 끊깁니다. 사용법 문제인지 불량인지 확인 부탁드려요.',
  }),
  createInquiry({
    inquiryNo: 'membership-inquiry-002',
    inquiryDate: '2026-04-29T09:30:00+09:00',
    productName: '긴급 문의 1건이 접수됐어요',
    title: '긴급 문의 1건이 접수됐어요',
    content: '긴급 문의 1건이 접수됐어요',
    qnaTypeCode: '긴급문의',
  }),
  ...completedInquiries,
];

const remapMembershipInquiryPreviewDates = (inquiries: InquiryItem[]) => {
  const dateWindow = createReviewPreviewDateWindow();

  return inquiries.map((inquiry) => ({
    ...inquiry,
    InquiryDate: mapMembershipPreviewDateToWindow(inquiry.InquiryDate, dateWindow),
    InquiryCommentList: (inquiry.InquiryCommentList ?? []).map((comment) => ({
      ...comment,
      InquiryCommentDate: mapMembershipPreviewDateToWindow(
        comment.InquiryCommentDate,
        dateWindow,
      ),
    })),
  }));
};

export const membershipInquiryPreviewCounts = {
  total: membershipInquiryPreviewList.length,
  notYet: membershipInquiryPreviewList.filter((inquiry) => inquiry.CheckedYn !== 'Y').length,
  completed: membershipInquiryPreviewList.filter((inquiry) => inquiry.CheckedYn === 'Y').length,
};

export const membershipInquiryPreviewTabCounts: {
  key: InquiryTabKey;
  label: string;
  count: number;
}[] = [
  { key: 'not-yet', label: '미답변', count: membershipInquiryPreviewCounts.notYet },
  { key: 'completed', label: '답변완료', count: membershipInquiryPreviewCounts.completed },
];

export const membershipReviewPreviewMarketCounts = membershipReviewInquiryPreviewMarkets.map(
  (market) => ({
    key: getMarketKey(market),
    orderCount:
      market.MarketId === SELECT_ALL_MARKET.MarketId || market.StoreId === primaryMarket.StoreId
        ? membershipReviewPreviewList.length
        : 0,
  }),
);

export const membershipInquiryPreviewMarketCounts = membershipReviewInquiryPreviewMarkets.map(
  (market) => ({
    key: getMarketKey(market),
    orderCount:
      market.MarketId === SELECT_ALL_MARKET.MarketId || market.StoreId === primaryMarket.StoreId
        ? membershipInquiryPreviewCounts.notYet
        : 0,
  }),
);

export const getMembershipReviewPreviewListByMarket = (market: MarketStoreListItem) => {
  if (market.MarketId === SELECT_ALL_MARKET.MarketId || market.StoreId === primaryMarket.StoreId) {
    return remapMembershipReviewPreviewDates(membershipReviewPreviewList);
  }

  return [];
};

export const membershipReviewPreviewDetailQuery = (
  market: MarketStoreListItem,
): UseQueryOptions<MembershipReviewPreviewDetailData> => ({
  queryKey: ['membershipReviewPreview', getMarketKey(market)],
  queryFn: (): MembershipReviewPreviewDetailData => ({
    data: [
      {
        ReportDetailList: getMembershipReviewPreviewListByMarket(market),
      },
    ],
  }),
});

export const membershipReviewPreviewStoreListResponse = {
  code: '200',
  message: 'success',
  data: [
    {
      StoreList: membershipReviewInquiryPreviewMarkets.slice(1),
    },
  ],
} satisfies CplatApiResponse<Array<{ StoreList: MarketStoreListItem[] }>>;

export type MembershipInquiryPreviewResponse = {
  data: Array<{
    StoreList: MarketStoreListItem[];
    InquiryList: InquiryItem[];
  }>;
};

export const membershipInquiryPreviewResponse: MembershipInquiryPreviewResponse = {
  data: [
    {
      StoreList: membershipReviewInquiryPreviewMarkets.slice(1),
      InquiryList: remapMembershipInquiryPreviewDates(membershipInquiryPreviewList),
    },
  ],
};

export const createMembershipInquiryPreviewResponse = (): MembershipInquiryPreviewResponse => ({
  data: [
    {
      StoreList: membershipReviewInquiryPreviewMarkets.slice(1),
      InquiryList: remapMembershipInquiryPreviewDates(membershipInquiryPreviewList),
    },
  ],
});

export const membershipReviewPreviewTemplateResponse = {
  data: [
    {
      ReviewCommentTemplateList: [],
    },
  ],
};

export const membershipInquiryPreviewTemplateResponse = {
  data: [
    {
      InquiryCommentTemplateList: [],
    },
  ],
};
